/**
 * Safe notification navigation utilities
 * Ensures notifications never navigate to invalid routes (404 errors)
 */

import type { Notification } from "@/types/notification";
import { isValidUuid } from "@/lib/utils/uuid";

// Valid route patterns that notifications can navigate to
const VALID_ROUTE_PATTERNS = [
  /^\/messages(\/[^/?]+)?$/, // /messages or /messages/{matchId}
  /^\/communities\/[^/?]+(\?.*)?$/, // /communities/{communityId} or with query params
  /^\/events\/create$/, // /events/create - hosting a new event
  /^\/events\/[^/?]+$/, // /events/{eventId}
  /^\/events\/[^/?]+\/payout-issue(\?.*)?$/, // /events/{eventId}/payout-issue
  /^\/opportunities\/[^/?]+(\/manage)?$/, // /opportunities/{opportunityId} or /opportunities/{opportunityId}/manage
  /^\/profile$/, // /profile - the member's own profile
  /^\/profiles\/[^/?]+$/, // /profiles/{userId} - view another user's profile
  /^\/admin\/users(\/[^/?]+)?$/, // /admin/users or /admin/users/{userId}
  /^\/admin\/moderation(\?.*)?$/, // /admin/moderation (queue + optional query for message flag)
  /^\/admin\/system(\?.*)?$/, // /admin/system (+ ?route= from the push health alert)
  // /admin/reports deliberately NOT listed: no such route exists, and having it here is what let a
  // notification link to a 404 look like a valid one. The reports queue is /admin/moderation.
  /^\/leaderboard$/, // /leaderboard
  /^\/dashboard$/, // /dashboard
  /^\/weekly-summary$/, // /weekly-summary
  /^\/notifications(\?.*)?$/, // /notifications or with query params
  /^\/upgrade$/, // /upgrade - the in-app plan screen
  // /pricing is kept alongside it because notifications sent before 2026-08-24 carry that
  // path. It is now the website pricing page, and the app rewrites it to /upgrade in
  // middleware, so an old deep link still lands on the plan screen rather than a dead end.
  /^\/pricing$/, // /pricing - legacy, rewritten to /upgrade inside the app
  /^\/settings$/, // /settings
  /^\/settings\/notifications$/, // /settings/notifications
  /^\/invite-friends$/, // /invite-friends
  /^\/ambassador-program$/, // /ambassador-program
  /^\/discover(\?.*)?$/, // /discover or /discover?profile=xxx
  /^\/referrals$/, // /referrals
  /^\/services\/[^/?]+\/manage$/, // /services/{serviceId}/manage
  /^\/opportunities$/, // /opportunities - legacy, rewritten to /discover?tab=opportunities
];

/**
 * Paths that were sent but have no page behind them, and where they should go instead.
 *
 * /opportunities is the one that mattered: there has never been a page at it (the list
 * is a tab on Discover), but it was on the allowlist, so notifications pointing at it
 * passed validation and then 404'd, which is worse than the silent fallback because the
 * member gets an error page instead of the wrong page. Rewriting rather than rejecting
 * means the notifications already sitting in members' lists start working too.
 */
const LEGACY_PATH_REWRITES: ReadonlyArray<[RegExp, string]> = [
  [/^\/opportunities$/, "/discover?tab=opportunities"],
];

/** Rewrites a known-dead path to its real destination, or returns it untouched. */
export function rewriteLegacyNotificationPath(url: string): string {
  const [path] = url.split("?");
  const cleanPath = path.split("#")[0];
  for (const [pattern, destination] of LEGACY_PATH_REWRITES) {
    if (pattern.test(cleanPath)) return destination;
  }
  return url;
}

/**
 * Converts FCM/APNs/universal-link strings into an app-internal path (pathname + search + hash)
 * for validation and router navigation. Handles https origins and growthsync:// custom scheme.
 */
export function normalizeDeepLinkToAppPath(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return null;
    }
  }

  const scheme = "growthsync://";
  if (trimmed.toLowerCase().startsWith(scheme)) {
    let rest = trimmed.slice(scheme.length);
    if (rest.startsWith("//")) {
      rest = rest.slice(2);
    }
    if (rest.startsWith("app/")) {
      rest = `/${rest.slice(4)}`;
    } else if (rest.length > 0 && !rest.startsWith("/")) {
      rest = `/${rest}`;
    }
    return rest || "/";
  }

  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  return null;
}

/**
 * Validates if a URL is safe to navigate to
 * Checks that the URL matches valid route patterns and contains valid UUIDs where expected
 */
export function isValidNotificationUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  // Normalize URL - remove leading/trailing whitespace and ensure it starts with /
  const normalized = url.trim();
  if (!normalized.startsWith("/")) {
    return false; // Only allow relative URLs
  }

  // Remove query params and hash for pattern matching
  const [path] = normalized.split("?");
  const cleanPath = path.split("#")[0];

  // Check if path matches any valid route pattern
  const matchesPattern = VALID_ROUTE_PATTERNS.some((pattern) => pattern.test(cleanPath));
  if (!matchesPattern) {
    return false;
  }

  // Static sub-routes that live under a parameterised segment. Without this the UUID
  // check below reads "create" in /events/create as a malformed event id and throws the
  // whole path out, which is how the "create an event" nudge ended up going nowhere.
  const STATIC_SUBROUTES = new Set(["/events/create"]);
  if (STATIC_SUBROUTES.has(cleanPath)) {
    return true;
  }

  // Extract UUIDs from the path and validate them
  // Paths like /messages/{uuid}, /events/{uuid}, /communities/{uuid}, /profiles/{uuid}, /opportunities/{uuid}, /admin/users/{userId}
  const uuidPaths = [
    /^\/messages\/([^/?]+)/,
    /^\/events\/([^/?]+)/,
    /^\/communities\/([^/?]+)/,
    /^\/profiles\/([^/?]+)/,
    /^\/opportunities\/([^/?]+)/,
    /^\/admin\/users\/([^/?]+)/,
  ];

  for (const uuidPattern of uuidPaths) {
    const match = cleanPath.match(uuidPattern);
    if (match && match[1]) {
      const potentialUuid = match[1];
      // Validate UUID format
      if (!isValidUuid(potentialUuid)) {
        return false; // Invalid UUID format
      }
    }
  }

  // Validate query params if present (e.g., /communities/{uuid}?post={uuid})
  if (normalized.includes("?")) {
    const queryString = normalized.split("?")[1].split("#")[0];
    const params = new URLSearchParams(queryString);
    
    // Check post and comment params (should be UUIDs if present)
    const postParam = params.get("post");
    const commentParam = params.get("comment");
    const profileParam = params.get("profile");
    if (postParam && !isValidUuid(postParam)) {
      return false; // Invalid UUID in query param
    }
    if (commentParam && !isValidUuid(commentParam)) {
      return false; // Invalid UUID in query param
    }
    if (profileParam && !isValidUuid(profileParam)) {
      return false; // Invalid UUID in profile param
    }
  }

  return true;
}

/**
 * Gets a safe navigation URL for a notification
 * Returns a valid URL or falls back to /notifications
 */
export function getSafeNotificationUrl(notification: Notification): string {
  const metadata = notification.metadata || {};
  const title = (notification.title || '').toLowerCase();

  // Force weekly digests to dedicated destination, including legacy notifications
  // that may still carry stale dashboard URLs.
  if (notification.type === "weekly_digest") {
    return "/weekly-summary";
  }

  // Payout issue notifications always route to the dedicated payout-issue page,
  // including legacy notifications that still carry the old /events/{id} URL.
  const titleLower = (notification.title || "").toLowerCase();
  const isPayoutIssue =
    notification.type === "system" &&
    titleLower.includes("payout") &&
    (titleLower.includes("issue") ||
      titleLower.includes("attention") ||
      titleLower.includes("failed") ||
      titleLower.includes("fix"));
  if (isPayoutIssue && metadata.eventId) {
    const eventId = String(metadata.eventId).trim();
    if (eventId) {
      const url = `/events/${eventId}/payout-issue`;
      if (isValidNotificationUrl(url)) return url;
    }
  }
  
  // Check if this is a "liked you" / "wants to sync" notification
  const isLikedYouNotification = 
    notification.type === "match_new" && 
    (metadata.likeId || metadata.likerProfileId || title.includes('liked') || title.includes('wants to sync'));
  
  // Priority 1: Use resolvedUrl (computed by backend) if valid, but fix /matches -> /discover
  if (notification.resolvedUrl) {
    // Fix invalid /matches route - convert to /discover for "liked you" notifications
    let resolvedUrl = notification.resolvedUrl;
    if (resolvedUrl === "/matches" || resolvedUrl.startsWith("/matches?")) {
      resolvedUrl = "/discover";
    }
    // For "liked you" notifications: if URL is plain /discover but we have likerProfileId, use deep link to Likes tab
    if (isLikedYouNotification && (resolvedUrl === "/discover" || resolvedUrl === "/discover/") && metadata.likerProfileId) {
      const profileId = String(metadata.likerProfileId).trim();
      if (isValidUuid(profileId)) {
        resolvedUrl = `/discover?tab=likes-you&profile=${profileId}`;
      }
    }
    if (isValidNotificationUrl(resolvedUrl)) {
      return resolvedUrl;
    }
  }

  // Priority 2: Use ctaUrl if valid, but fix /matches -> /discover
  if (notification.ctaUrl) {
    // Fix invalid /matches route - convert to /discover for "liked you" notifications
    let ctaUrl = notification.ctaUrl;
    if (ctaUrl === "/matches" || ctaUrl.startsWith("/matches?")) {
      ctaUrl = "/discover";
    }
    // For "liked you" notifications: if URL is plain /discover but we have likerProfileId, use deep link to Likes tab
    if (isLikedYouNotification && (ctaUrl === "/discover" || ctaUrl === "/discover/") && metadata.likerProfileId) {
      const profileId = String(metadata.likerProfileId).trim();
      if (isValidUuid(profileId)) {
        ctaUrl = `/discover?tab=likes-you&profile=${profileId}`;
      }
    }
    if (isValidNotificationUrl(ctaUrl)) {
      return ctaUrl;
    }
  }

  // Priority 3: Compute URL from metadata if possible

  // "Liked you" notifications - route to Discover → Likes tab with specific profile so user can like them back
  if (isLikedYouNotification && metadata.likerProfileId) {
    const profileId = String(metadata.likerProfileId).trim();
    if (isValidUuid(profileId)) {
      return `/discover?tab=likes-you&profile=${profileId}`;
    }
  }
  if (isLikedYouNotification) {
    return "/discover";
  }
  
  // Match-related notifications (with specific matchId or conversationId)
  if (metadata.conversationId) {
    // Priority: Use conversationId if available (more reliable)
    const conversationId = String(metadata.conversationId).trim();
    // Note: Messages are currently linked via match_id, but we can use matchId to navigate
    // If we have both, prefer matchId for navigation
    if (metadata.matchId) {
      const matchId = String(metadata.matchId).trim();
      const url = `/messages/${matchId}`;
      if (isValidNotificationUrl(url)) {
        return url;
      }
    }
  }
  
  if (metadata.matchId) {
    const matchId = String(metadata.matchId).trim();
    const url = `/messages/${matchId}`;
    if (isValidNotificationUrl(url)) {
      return url;
    }
  }

  // Community-related notifications (join, post, comment, etc.)
  if (metadata.communityId) {
    const communityId = String(metadata.communityId).trim();
    // Check if it's a specific post notification
    if (metadata.postId) {
      const postId = String(metadata.postId).trim();
      const url = `/communities/${communityId}?post=${postId}`;
      if (isValidNotificationUrl(url)) {
        return url;
      }
    }
    // Check if it's a specific comment notification
    if (metadata.commentId) {
      const commentId = String(metadata.commentId).trim();
      const url = `/communities/${communityId}?comment=${commentId}`;
      if (isValidNotificationUrl(url)) {
        return url;
      }
    }
    // All other community notifications (join, leave, etc.) go to the community page
    const url = `/communities/${communityId}`;
    if (isValidNotificationUrl(url)) {
      return url;
    }
  }
  
  // Also check title/description for community join/leave notifications that might not have communityId in metadata
  // (This is a fallback for edge cases)
  if ((title.includes('joined') || title.includes('left')) && 
      (title.includes('community') || notification.description?.toLowerCase().includes('community'))) {
    // If we have a communityId in metadata, use it (already handled above, but this is a safety check)
    if (metadata.communityId) {
      const communityId = String(metadata.communityId).trim();
      const url = `/communities/${communityId}`;
      if (isValidNotificationUrl(url)) {
        return url;
      }
    }
  }

  // event_review: attendee prompt vs host "new review" — avoid generic /events/{id}
  if (notification.type === "event_review" && metadata.eventId) {
    const eventId = String(metadata.eventId).trim();
    if (isValidUuid(eventId)) {
      const suffix =
        metadata.promptType === "post_event_review" ? "?action=review" : "?action=reviews";
      const url = `/events/${eventId}${suffix}`;
      if (isValidNotificationUrl(url)) {
        return url;
      }
    }
  }

  // Event-related notifications
  if (metadata.eventId && notification.type !== "event_review") {
    const eventId = String(metadata.eventId).trim();
    const url = `/events/${eventId}`;
    if (isValidNotificationUrl(url)) {
      return url;
    }
  }

  // Opportunity-related notifications (filled, application, etc.)
  if (metadata.opportunityId) {
    const opportunityId = String(metadata.opportunityId).trim();
    const url = `/opportunities/${opportunityId}`;
    if (isValidNotificationUrl(url)) {
      return url;
    }
  }

  // Admin notifications - check for admin_user_* types first
  if (notification.type.startsWith("admin_")) {
    if (notification.type === "admin_user_signed_up" || notification.type === "admin_user_onboarded" || notification.type === "admin_user_verified") {
      if (metadata.userId) {
        const userId = String(metadata.userId).trim();
        const url = `/admin/users/${userId}`;
        if (isValidNotificationUrl(url)) {
          return url;
        }
      }
      // Fallback to users list if no userId
      return "/admin/users";
    }
    if (notification.type === "admin_subscription_started" || notification.type === "admin_subscription_cancelled" || notification.type === "admin_payment_failed") {
      if (metadata.userId) {
        const userId = String(metadata.userId).trim();
        const url = `/admin/users/${userId}`;
        if (isValidNotificationUrl(url)) {
          return url;
        }
      }
      return "/admin/users";
    }
  }

  // Profile/user-related notifications
  if (metadata.userId || metadata.actorUserId) {
    const userId = String(metadata.userId || metadata.actorUserId).trim();
    const url = `/profiles/${userId}`;
    if (isValidNotificationUrl(url)) {
      return url;
    }
  }

  // Match notifications without specific matchId - navigate to discover page
  // (This handles cases where it's a "liked you" / "wants to sync" but we didn't catch it above)
  if (notification.type === "match_new") {
    // Check if title suggests it's a "liked you" / "wants to sync" notification
    if (title.includes('liked') || title.includes('wants to sync')) {
      return "/discover";
    }
    // Otherwise, navigate to messages page
    return "/messages";
  }

  // Leaderboard notifications
  if (
    notification.type === "system" &&
    (notification.title?.toLowerCase().includes("leaderboard") ||
      metadata.context?.toString().toLowerCase().includes("leaderboard"))
  ) {
    return "/leaderboard";
  }

  // Default fallback: always return a valid page (notifications page)
  return "/notifications";
}

/**
 * Safely navigates to a notification destination
 * Always ensures navigation to a valid page, never a 404
 */
export function getNotificationNavigationUrl(notification: Notification): string {
  const url = rewriteLegacyNotificationPath(getSafeNotificationUrl(notification));

  // Double-check the URL is valid before returning
  if (isValidNotificationUrl(url)) {
    return url;
  }
  
  // Final fallback - should never happen, but ensures we never return an invalid URL
  return "/notifications";
}

/**
 * Returns the URL if it is a valid deep-link path, otherwise the notifications page.
 * Use for push notification tap handling when you only have a raw URL string.
 */
export function getSafeDeepLinkUrl(url: string | null | undefined): string {
  const resolved = normalizeDeepLinkToAppPath(url) ?? url?.trim() ?? "";
  const normalized = resolved ? rewriteLegacyNotificationPath(resolved) : resolved;
  if (normalized && isValidNotificationUrl(normalized)) {
    return normalized.trim();
  }
  return "/notifications";
}
